import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, Group, NumberInput, Table, Text, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";

import { api, ApiClientError } from "../api/client";

function dur(seconds: number): string {
  if (seconds <= 0) return "off";
  if (seconds >= 86400) return `${(seconds / 86400).toFixed(seconds % 86400 ? 1 : 0)} d`;
  return `${(seconds / 3600).toFixed(seconds % 3600 ? 1 : 0)} h`;
}

/**
 * Settings → Kronos: the operator ceiling on how far back Kronos cache +
 * candle data must stay in the database, with each market's requirement
 * (from its active config and fleet assignments) and what is currently held.
 * Twin of V3's card; the command center has one PostgreSQL, so "held" means
 * not yet archived by the Storage page.
 */
export function KronosHorizonCard() {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: ["storage", "status"], queryFn: () => api.storage.status(), refetchInterval: 10000 });
  const [days, setDays] = useState<number | null>(null);
  const save = useMutation({
    mutationFn: (v: number) => api.storage.updateSettings({ kronos_horizon_days: v }),
    onSuccess: () => {
      setDays(null);
      void qc.invalidateQueries({ queryKey: ["storage"] });
      notifications.show({ color: "green", title: "Kronos", message: "Data horizon saved" });
    },
    onError: (err) => notifications.show({ color: "red", title: "Kronos", message: err instanceof ApiClientError ? err.message : String(err) }),
  });
  const s = status.data;
  if (!s) return null;
  const current = days ?? s.settings.kronos_horizon_days;
  const offending = s.floors.filter((f) => !f.within_ceiling);
  return (
    <Card withBorder>
      <Group justify="space-between" align="flex-end">
        <div>
          <Title order={5}>Data horizon</Title>
          <Text size="sm" c="dimmed">
            How far back Kronos cache and candle data are kept in the database and precomputed by default (cap {s.settings.kronos_horizon_cap_days} d).
            The requirement of each active config is enforced automatically; this is the ceiling. Months inside the horizon cannot be archived.
          </Text>
        </div>
        <Group align="flex-end">
          <NumberInput label="Days" w={120} min={1} max={s.settings.kronos_horizon_cap_days} value={current} onChange={(v) => setDays(Number(v))} />
          <Button disabled={days == null || days === s.settings.kronos_horizon_days} loading={save.isPending} onClick={() => days != null && save.mutate(days)}>Save</Button>
        </Group>
      </Group>
      {offending.length > 0 && (
        <Alert color="red" mt="sm" title="Active config exceeds the horizon">
          {offending.map((f) => `${f.market_id} needs ${dur(f.requirement_seconds)}`).join("; ")} — raise the horizon or shorten the lookback.
        </Alert>
      )}
      <Table mt="sm" striped>
        <Table.Thead>
          <Table.Tr><Table.Th>Market</Table.Th><Table.Th>Required by active config</Table.Th><Table.Th>Held candles from</Table.Th><Table.Th>Held cache from</Table.Th><Table.Th /></Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {s.floors.map((f) => (
            <Table.Tr key={f.market_id}>
              <Table.Td>{f.market_id}</Table.Td>
              <Table.Td>{dur(f.requirement_seconds)}</Table.Td>
              <Table.Td>{f.held_candles_from ?? "—"}</Table.Td>
              <Table.Td>{f.held_kronos_from ?? "—"}</Table.Td>
              <Table.Td>{f.within_ceiling ? <Badge size="xs" color="green">ok</Badge> : <Badge size="xs" color="red">exceeds horizon</Badge>}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  );
}
